import { AxiosError } from "axios";
import dayjs, { Dayjs } from "dayjs";
import { scheduling, schedulingV2 } from "lobbie";
import { createContext, useCallback, useContext, useMemo, useState } from "react";
import {
    IAvailabilityResultV2,
    ISelfSchedule,
    ISelfScheduleSettings,
} from "src/components/patients/scheduling/types";
import { BASE_PUBLIC_API_ROUTE, ROUTES } from "src/constants";
import { ENotificationSendMethod } from "src/constants/notifications";
import { handleError, isEmptyObject, logDev, notify, zeroTime } from "src/utils";
import { useAxiosPublicPost } from "../useAxios";

type PatientSelfSchedulingType = {
    hasFutureAvailability: boolean; // TODO: this needs to be reset if user goes back to location, appt type, or practitioner
    selfScheduleSettings: ISelfScheduleSettings | null;
    setSelfScheduleSettings: React.Dispatch<React.SetStateAction<ISelfScheduleSettings | null>>;
    selfSchedule: ISelfSchedule | null;
    setSelfSchedule: React.Dispatch<React.SetStateAction<ISelfSchedule | null>>;
    availability: IAvailabilityResultV2;
    setAvailability: React.Dispatch<React.SetStateAction<IAvailabilityResultV2>>;
    route: string | null;
    getMonth: (startUnix: number, endUnix: number) => void;
    setRoute: React.Dispatch<React.SetStateAction<string | null>>;
    practitioners: scheduling.IPractitionerNextAvailable[];
    setPractitioners: React.Dispatch<React.SetStateAction<scheduling.IPractitionerNextAvailable[]>>;
    getNextRoute: () => string;
    minDate: Dayjs;
    maxDate: Dayjs;
};

export const PatientSelfSchedulingContext = createContext<PatientSelfSchedulingType | undefined>(
    undefined,
);

export default function PatientSelfSchedulingContextProvider({
    children,
}: {
    children: React.ReactNode;
}) {
    const [hasFutureAvailability, setHasFutureAvailability] = useState(true);
    const [selfScheduleSettings, setSelfScheduleSettings] = useState<ISelfScheduleSettings | null>(
        null,
    );
    const [selfSchedule, setSelfSchedule] = useState<ISelfSchedule | null>(null);
    const [availability, setAvailability] = useState<IAvailabilityResultV2>({
        selectedMonth: dayjs().month(),
        selectedYear: dayjs().year(),
        schedule: {
            slotsByDate: {},
            practitioners: [],
            hasAnyFutureAvailability: false,
            arePractitionersSelectable: false,
            timezone: "",
        },
    } as IAvailabilityResultV2);
    const [route, setRoute] = useState<string | null>(null);
    const [practitioners, setPractitioners] = useState<scheduling.IPractitionerNextAvailable[]>([]);
    const [initialLoad, setInitialLoad] = useState(true);
    const poster = useAxiosPublicPost();

    const getPractitionerIds = useCallback(() => {
        if (!selfSchedule?.location || !selfScheduleSettings?.locationPractitioners) return [];
        const locationPractitioners =
            selfScheduleSettings?.locationPractitioners[selfSchedule.location.id];
        if (!locationPractitioners) return [];
        const practitionerIds: number[] = [];
        Object.keys(locationPractitioners).map((locationId) => {
            practitionerIds.push(locationPractitioners[locationId].practitionerId);
        });
        return practitionerIds;
    }, [selfSchedule?.location, selfScheduleSettings?.locationPractitioners]);

    const handleSetPractitioners = useCallback(
        (_practitioners: schedulingV2.self.ISelfSchedulePractitioner[]) => {
            const practitionersToRender: scheduling.IPractitionerNextAvailable[] = [];
            for (const _practitioner of _practitioners) {
                practitionersToRender.push({
                    id: _practitioner.practitionerId,
                    displayName: _practitioner.displayName,
                    nextAvailableStartUnix: _practitioner.firstAvailableSlot,
                });
            }
            setPractitioners(practitionersToRender);
        },
        [setPractitioners],
    );

    logDev("availability", availability);

    const getMonth = useCallback(
        (startUnix: number, endUnix: number) => {
            if (!selfSchedule?.location || !selfSchedule?.appointmentType) {
                return;
            }
            const route_ = `${BASE_PUBLIC_API_ROUTE}/patientscheduling-v2/get-appointment-available-slots`;
            poster(route_, {
                locationId: selfSchedule.location?.id,
                practitionerIds: getPractitionerIds(),
                appointmentTypeId: selfSchedule.appointmentType?.id,
                startUnix,
                endUnix,
                trackingUuid: selfScheduleSettings?.trackingUuid,
                accountPublicUuid: selfScheduleSettings?.accountPublicUuid,
                selfSchedulingSettingPublicUuid:
                    selfScheduleSettings?.selfSchedulingSettingPublicUuid,
            })
                .then((response) => {
                    const result =
                        (response?.data as schedulingV2.self.ISelfSchedulingAvailabilities) || {
                            slots: [],
                            hasAnyFutureAvailability: false,
                        };
                    setHasFutureAvailability(result.hasAnyFutureAvailability);
                    logDev("availability result", result);
                    if (!isEmptyObject(result.slotsByDate)) {
                        setInitialLoad(false);
                        setAvailability((prev) => {
                            return {
                                ...prev,
                                schedule: result,
                            };
                        });
                        handleSetPractitioners(result.practitioners);
                    } else if (result.hasAnyFutureAvailability && initialLoad) {
                        // Move to next month only if it's initial load and there's future availability
                        const nextMonth = dayjs(startUnix).add(1, "month");
                        setAvailability((prev) => {
                            return {
                                ...prev,
                                selectedMonth: nextMonth.month(),
                                selectedYear: nextMonth.year(),
                            };
                        });
                        getMonth(
                            zeroTime(nextMonth).valueOf(),
                            zeroTime(nextMonth).add(1, "month").valueOf(),
                        );
                    } else {
                        setInitialLoad(false);
                        notify({
                            level: "warning",
                            title: `No times available.`,
                            message: "No available slots found for the selected criteria.",
                        });
                    }
                })
                .catch((error: AxiosError) => {
                    handleError(error);
                    setInitialLoad(false);
                });
        },
        [
            selfSchedule,
            poster,
            getPractitionerIds,
            selfScheduleSettings,
            initialLoad,
            handleSetPractitioners,
        ],
    );

    const minDate = useMemo(() => {
        if (selfScheduleSettings?.forwardBookingLimit) {
            if (selfScheduleSettings?.forwardBookingLimitUnit) {
                return dayjs().add(
                    selfScheduleSettings.forwardBookingLimit,
                    selfScheduleSettings.forwardBookingLimitUnit,
                );
            }
        }
        return dayjs();
    }, [selfScheduleSettings?.forwardBookingLimit, selfScheduleSettings?.forwardBookingLimitUnit]);

    const maxDate = useMemo(() => {
        if (selfScheduleSettings?.afterwardBookingLimit) {
            if (selfScheduleSettings?.afterwardBookingLimitUnit) {
                return dayjs().add(
                    selfScheduleSettings.afterwardBookingLimit,
                    selfScheduleSettings.afterwardBookingLimitUnit,
                );
            }
        }
        return dayjs().add(1, "year");
    }, [
        selfScheduleSettings?.afterwardBookingLimit,
        selfScheduleSettings?.afterwardBookingLimitUnit,
    ]);

    const getNextRoute = useCallback(() => {
        if (!selfSchedule) {
            return ROUTES.patientSchedulingApptType;
        }
        if (route === ROUTES.patientSchedulingApptType) {
            if (selfScheduleSettings?.locationSelectable) {
                return ROUTES.patientSchedulingLocation;
            } else if (selfScheduleSettings?.practitionerSelectable) {
                return ROUTES.patientSchedulingPractitioner;
            }
            return ROUTES.patientSchedulingCalendar;
        }
        if (route === ROUTES.patientSchedulingLocation) {
            if (selfScheduleSettings?.practitionerSelectable) {
                return ROUTES.patientSchedulingPractitioner;
            }
            return ROUTES.patientSchedulingCalendar;
        }
        if (route === ROUTES.patientSchedulingPractitioner) {
            return ROUTES.patientSchedulingCalendar;
        }
        if (route === ROUTES.patientSchedulingCalendar) {
            if (selfScheduleSettings?.verificationMethod === ENotificationSendMethod.Sms) {
                return ROUTES.patientSchedulingSMSConfirmation;
            } else {
                return ROUTES.patientSchedulingUserEmail;
            }
        }
        if (route === ROUTES.patientSchedulingUserEmail) {
            if (selfScheduleSettings?.verificationMethod === ENotificationSendMethod.EmailAndSms) {
                return ROUTES.patientSchedulingSMSConfirmation;
            }
            return ROUTES.patientSchedulingUserInfo;
        }
        if (route === ROUTES.patientSchedulingSMSConfirmation) {
            return ROUTES.patientSchedulingUserInfo;
        }
        if (route === ROUTES.patientSchedulingUserInfo) {
            return ROUTES.patientSchedulingConfirmation;
        }
        if (route === ROUTES.patientSchedulingConfirmation) {
            return ROUTES.patientSchedulingBookingConfirmed;
        }
        return ROUTES.patientSchedulingCalendar;
    }, [route, selfSchedule, selfScheduleSettings]);

    return (
        <PatientSelfSchedulingContext.Provider
            value={{
                hasFutureAvailability,
                selfScheduleSettings,
                setSelfScheduleSettings,
                selfSchedule,
                setSelfSchedule,
                availability,
                setAvailability,
                route,
                getMonth,
                setRoute,
                practitioners,
                setPractitioners,
                getNextRoute,
                minDate,
                maxDate,
            }}
        >
            {children}
        </PatientSelfSchedulingContext.Provider>
    );
}

// eslint-disable-next-line react-refresh/only-export-components
export const usePatientSelfSchedulingContext = () => {
    const context = useContext(PatientSelfSchedulingContext);
    if (!context) {
        throw new Error(
            "usePatientSelfSchedulingContext must be used within a PatientSelfSchedulingProvider",
        );
    }
    return context;
};
